import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  title: "", sport: "football", year: new Date().getFullYear(),
  set_name: "", player: "", team: "", brand: "", brand_line: "",
  parallel: "", serial_number: "",
  price: 0, compare_at_price: null, stock: 1, images: [], category: "singles",
  attributes: [], condition: "raw", grader: "raw", description: "",
  pack_tier: "", pack_label: "", pack_sub: "",
  floor_value: null, ceiling_value: null, odds: null,
};

const CONDITIONS = ["raw", "psa10", "psa9", "psa8", "bgs95", "sgc10"];
const PACK_TIERS = ["", "single", "5pack", "20case"];

export default function ProductForm({ initial, onSaved, onCancel }) {
  const [p, setP] = useState({ ...EMPTY, ...(initial || {}) });
  // Local array-of-pairs mirror for the odds editor so duplicate/blank keys don't collide.
  // Synced to p.odds only on submit + on remove.
  const [oddsRows, setOddsRows] = useState(() =>
    Object.entries((initial && initial.odds) || {}).map(([k, v]) => ({ k, v }))
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sportOpts, setSportOpts] = useState([]);
  const [attrOpts, setAttrOpts] = useState([]);
  const [labelOpts, setLabelOpts] = useState([]);
  const [subOpts, setSubOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);
  const [brandLineOpts, setBrandLineOpts] = useState([]);
  const [graderOpts, setGraderOpts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/filters?kind=sport"),
      api.get("/filters?kind=attribute"),
      api.get("/filters?kind=pack_label"),
      api.get("/filters?kind=pack_sub"),
      api.get("/filters?kind=brand"),
      api.get("/filters?kind=brand_line"),
      api.get("/filters?kind=grader"),
    ]).then(([s, a, l, sub, b, bl, g]) => {
      setSportOpts(s.data); setAttrOpts(a.data); setLabelOpts(l.data);
      setSubOpts(sub.data); setBrandOpts(b.data); setBrandLineOpts(bl.data);
      setGraderOpts(g.data);
    }).catch((err) => console.warn("Filter load:", err?.message));
  }, []);

  const set = (k, v) => setP((cur) => ({ ...cur, [k]: v }));
  const toggleAttr = (a) => setP((cur) => ({
    ...cur, attributes: cur.attributes.includes(a) ? cur.attributes.filter((x) => x !== a) : [...cur.attributes, a],
  }));

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${BACKEND_URL}${r.data.url}`;
      set("images", [...p.images, fullUrl]);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Collapse the odds editor rows into a dict, dropping blank keys.
      const oddsDict = {};
      for (const { k, v } of oddsRows) {
        const key = (k || "").trim();
        if (key) oddsDict[key] = v;
      }
      const payload = { ...p, price: parseFloat(p.price), stock: parseInt(p.stock, 10) || 0,
                         compare_at_price: p.compare_at_price !== null && p.compare_at_price !== "" ? parseFloat(p.compare_at_price) : null,
                         year: p.year ? parseInt(p.year, 10) : null,
                         floor_value: p.floor_value ? parseFloat(p.floor_value) : null,
                         ceiling_value: p.ceiling_value ? parseFloat(p.ceiling_value) : null,
                         odds: Object.keys(oddsDict).length ? oddsDict : null };
      if (initial?.id) {
        await api.put(`/admin/products/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 border border-white/10 rounded p-6 bg-[var(--cl-surface)]" data-testid="product-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl">{initial?.id ? "Edit" : "New"} Product</h3>
        <button type="button" onClick={onCancel} className="text-zinc-500 hover:text-white"><X size={18} /></button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Category">
          <select value={p.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" className="input">
            <option value="singles">Single Card</option>
            <option value="mystery_pack">Mystery Pack</option>
            <option value="sealed_pack">Sealed Pack</option>
            <option value="sealed_box">Sealed Box</option>
            <option value="sealed_case">Sealed Case</option>
            <option value="merch">Merch</option>
          </select>
        </Field>
        <Field label="Title *">
          <input required value={p.title} onChange={(e) => set("title", e.target.value)} data-testid="pf-title" className="input" />
        </Field>
        <Field label="Sport">
          <select value={p.sport} onChange={(e) => set("sport", e.target.value)} data-testid="pf-sport" className="input">
            <option value="multi">multi</option>
            {sportOpts.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Price ($) — NOW *">
          <input required type="number" step="0.01" value={p.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" className="input" />
        </Field>
        <Field label="Was price ($) — optional, shown red with strikethrough above NOW">
          <input type="number" step="0.01" value={p.compare_at_price ?? ""} placeholder="Leave blank if not on sale"
                 onChange={(e) => set("compare_at_price", e.target.value === "" ? null : e.target.value)}
                 data-testid="pf-was-price" className="input" />
        </Field>
        <Field label="Stock">
          <input type="number" value={p.stock} onChange={(e) => set("stock", e.target.value)} data-testid="pf-stock" className="input" />
        </Field>
        <Field label="Year">
          <input type="number" value={p.year || ""} onChange={(e) => set("year", e.target.value)} data-testid="pf-year" className="input" />
        </Field>
        <Field label="Set">
          <input value={p.set_name || ""} onChange={(e) => set("set_name", e.target.value)} data-testid="pf-set" className="input" />
        </Field>
        <Field label="Player">
          <input value={p.player || ""} onChange={(e) => set("player", e.target.value)} data-testid="pf-player" className="input" />
        </Field>
        <Field label="Team">
          <input value={p.team || ""} onChange={(e) => set("team", e.target.value)} data-testid="pf-team" placeholder="e.g. Chiefs" className="input" />
        </Field>
        <Field label="Brand">
          <select value={p.brand || ""} onChange={(e) => { set("brand", e.target.value); set("brand_line", ""); }} data-testid="pf-brand" className="input">
            <option value="">—</option>
            {brandOpts.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </Field>
        <Field label="Brand Set / Line">
          <select value={p.brand_line || ""} onChange={(e) => set("brand_line", e.target.value)} data-testid="pf-brand-line" className="input">
            <option value="">—</option>
            {brandLineOpts.filter((bl) => !p.brand || bl.parent_key === p.brand).map((bl) => (
              <option key={bl.key} value={bl.key}>{bl.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Parallel">
          <input value={p.parallel || ""} onChange={(e) => set("parallel", e.target.value)} data-testid="pf-parallel" className="input" />
        </Field>
        <Field label="Serial (e.g. /75)">
          <input value={p.serial_number || ""} onChange={(e) => set("serial_number", e.target.value)} data-testid="pf-serial" className="input" />
        </Field>
        <Field label="Grader">
          <select value={p.grader || ""} onChange={(e) => set("grader", e.target.value)} data-testid="pf-grader" className="input">
            <option value="">—</option>
            {graderOpts.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </Field>
        <Field label="Condition / Grade">
          <input value={p.condition || ""} onChange={(e) => set("condition", e.target.value)} data-testid="pf-condition" placeholder="raw, psa10, bgs9.5..." className="input" />
        </Field>
        {p.category === "mystery_pack" && (
          <Field label="Pack Tier (size)">
            <select value={p.pack_tier || ""} onChange={(e) => set("pack_tier", e.target.value)} data-testid="pf-pack-tier" className="input">
              {PACK_TIERS.map((t) => <option key={t} value={t}>{t || "—"}</option>)}
            </select>
          </Field>
        )}
        {p.category === "mystery_pack" && (
          <Field label="Pack Label">
            <select value={p.pack_label || ""} onChange={(e) => { set("pack_label", e.target.value); set("pack_sub", ""); }} data-testid="pf-pack-label" className="input">
              <option value="">—</option>
              {labelOpts.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </Field>
        )}
        {p.category === "mystery_pack" && p.pack_label && (
          <Field label="Pack Sub-Category">
            <select value={p.pack_sub || ""} onChange={(e) => set("pack_sub", e.target.value)} data-testid="pf-pack-sub" className="input">
              <option value="">—</option>
              {subOpts.filter((s) => s.parent_key === p.pack_label).map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </Field>
        )}
        {p.category === "mystery_pack" && <>
          <Field label="Floor Value ($)"><input type="number" step="0.01" value={p.floor_value || ""} onChange={(e) => set("floor_value", e.target.value)} className="input" /></Field>
          <Field label="Ceiling Value ($)"><input type="number" step="0.01" value={p.ceiling_value || ""} onChange={(e) => set("ceiling_value", e.target.value)} className="input" /></Field>
        </>}
      </div>

      {p.category === "mystery_pack" && (
        <div data-testid="pf-odds-editor">
          <div className="text-label text-zinc-500 mb-2">Pack Criteria / Odds</div>
          <p className="text-xs text-zinc-500 mb-3">Displayed as rows on each pack card (e.g. "auto_rate": "1 in 8", "hit_floor": "$50 min").</p>
          <div className="space-y-2">
            {oddsRows.map((row, idx) => (
              <div key={idx} className="flex gap-2" data-testid={`pf-odds-row-${idx}`}>
                <input value={row.k} placeholder="label (e.g. auto_rate)"
                       onChange={(e) => setOddsRows((cur) => cur.map((r, i) => i === idx ? { ...r, k: e.target.value } : r))}
                       data-testid={`pf-odds-key-${idx}`}
                       className="input flex-1" />
                <input value={row.v} placeholder="value (e.g. 1 in 8)"
                       onChange={(e) => setOddsRows((cur) => cur.map((r, i) => i === idx ? { ...r, v: e.target.value } : r))}
                       data-testid={`pf-odds-val-${idx}`}
                       className="input flex-1" />
                <button type="button"
                        onClick={() => setOddsRows((cur) => cur.filter((_, i) => i !== idx))}
                        data-testid={`pf-odds-remove-${idx}`}
                        className="text-zinc-500 hover:text-[var(--cl-primary)] px-2"
                        aria-label="Remove row">
                  <X size={14} />
                </button>
              </div>
            ))}
            <button type="button"
                    onClick={() => setOddsRows((cur) => [...cur, { k: "", v: "" }])}
                    data-testid="pf-odds-add"
                    className="btn-outline text-xs">
              + Add criterion row
            </button>
          </div>
        </div>
      )}

      <Field label="Description">
        <textarea rows={3} value={p.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" className="input" />
      </Field>

      <div>
        <div className="text-label text-zinc-500 mb-2">Tags / Attributes</div>
        <div className="flex flex-wrap gap-2">
          {attrOpts.map((a) => (
            <button key={a.key} type="button" onClick={() => toggleAttr(a.key)} data-testid={`pf-attr-${a.key}`}
                    className={`text-xs px-3 py-1 rounded border transition-colors ${p.attributes.includes(a.key) ? "bg-white text-black border-white" : "border-white/15 text-zinc-300"}`}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-label text-zinc-500 mb-2">Images</div>
        <div className="flex flex-wrap gap-3">
          {p.images.map((img) => (
            <div key={img} className="relative w-24 h-32 rounded border border-white/10 overflow-hidden group">
              <img src={img} className="w-full h-full object-cover" alt="" />
              <button type="button" onClick={() => set("images", p.images.filter((i) => i !== img))}
                      className="absolute top-1 right-1 bg-black/80 rounded p-1 opacity-0 group-hover:opacity-100">
                <X size={12} />
              </button>
            </div>
          ))}
          <label className="w-24 h-32 rounded border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-[var(--cl-primary)]" data-testid="pf-upload-btn">
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                   onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
            {uploading ? <span className="text-xs text-zinc-500">…</span> : <UploadCloud size={20} className="text-zinc-400" />}
          </label>
        </div>
        <p className="text-xs text-zinc-500 mt-2">Max 8MB · JPEG/PNG/WebP</p>
      </div>

      <div className="flex gap-3 pt-4 border-t border-white/10">
        <button type="submit" disabled={saving} className="btn-primary" data-testid="pf-submit">
          {saving ? "Saving…" : (initial?.id ? "Save Changes" : "Create Product")}
        </button>
        <button type="button" onClick={onCancel} className="btn-outline">Cancel</button>
      </div>

      <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-label text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
